/**
 * WebSocket Hook for Job Paste Real-Time Updates
 *
 * This hook provides real-time WebSocket functionality for the Job Paste screen,
 * enabling instant updates when jobs are created, edited, or deleted by other users.
 * It integrates with the global WebSocket service and manages message handling
 * for all job-related WebSocket events.
 *
 * Key Features:
 * - Real-time job updates from other users
 * - Automatic UI updates without API calls
 * - User-specific notification filtering
 * - Connection management and error handling
 * - Support for all job operation types
 *
 * Architecture:
 * - Uses global WebSocket service for connection management
 * - Integrates with JobPasteStore for state updates
 * - Handles both individual and bulk job operations
 * - Provides user-friendly toast notifications
 * - Supports editing conflict detection
 *
 * Message Flow:
 * 1. WebSocket receives message from backend
 * 2. Message is routed to appropriate handler based on type
 * 3. Handler updates JobPasteStore with new data
 * 4. UI automatically updates via MobX reactivity
 * 5. Toast notifications shown for user awareness
 *
 * Usage:
 * ```tsx
 * const { sendEditingStatus, isConnected } = useJobPasteWebSocket();
 * ```
 */

import { useStore } from 'hooks/useStore';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { activeUsersWebSocketService } from 'services/activeUsersWebSocketService';
import { globalWebSocketService } from 'services/globalWebSocketService';

/**
 * Interface for job update messages from WebSocket
 * Used when individual jobs are updated by other users
 */
interface JobUpdateMessage {
  type: 'job_updated';
  data: any;
  timestamp: string;
  userId?: string;
}

/**
 * Interface for job creation messages from WebSocket
 * Used when new jobs are created by other users
 */
interface JobCreatedMessage {
  type: 'job_created';
  data: any;
  timestamp: string;
  userId?: string;
}

/**
 * Interface for job deletion messages from WebSocket
 * Used when jobs are deleted by other users
 * Contains complete updated jobs data for immediate UI refresh
 */
interface JobDeletedMessage {
  type: 'job_deleted';
  data: {
    jobId: string;
    updatedBy: string;
    completeJobsData?: any[]; // Complete jobs data for immediate update
  };
  timestamp: string;
  userId?: string;
}

/**
 * Interface for room update messages from WebSocket
 * Used when room-level changes occur
 */
interface RoomUpdateMessage {
  type: 'room_updated';
  data: any;
  timestamp: string;
  userId?: string;
}

/**
 * Interface for connection status messages from WebSocket
 * Used for connection health monitoring
 */
interface ConnectionStatusMessage {
  type: 'connection_status';
  data: any;
  timestamp: string;
  userId?: string;
}

/**
 * Interface for error messages from WebSocket
 * Used when WebSocket encounters errors
 */
interface ErrorMessage {
  type: 'error';
  message: string;
  timestamp: string;
  userId?: string;
}

/**
 * Interface for connection establishment messages from WebSocket
 * Sent when WebSocket connection is successfully established
 */
interface ConnectionEstablishedMessage {
  type: 'connection_established';
  message: string;
  timestamp: string;
  userId?: string;
  connection_id?: string;
  username?: string;
}

/**
 * Interface for subscription confirmation messages from WebSocket
 * Sent when user successfully subscribes to job updates
 */
interface SubscriptionConfirmedMessage {
  type: 'subscription_confirmed';
  message: string;
  timestamp: string;
  room?: string;
}

/**
 * Interface for heartbeat response messages from WebSocket
 * Sent in response to client heartbeat messages
 */
interface HeartbeatResponseMessage {
  type: 'heartbeat_response';
  message: string;
  timestamp: string;
}

/**
 * Interface for editing status confirmation messages from WebSocket
 * Sent when user editing status is confirmed by server
 */
interface EditingStatusConfirmedMessage {
  type: 'editing_status_confirmed';
  message: string;
  jobId: string;
  roomId: string;
  timestamp: string;
}

/**
 * Interface for user identification confirmation messages from WebSocket
 * Sent when user identification is confirmed by server
 */
interface UserIdentificationConfirmedMessage {
  type: 'user_identification_confirmed';
  message: string;
  userId: string;
  department: string;
  timestamp: string;
}

/**
 * Interface for active users updated messages from WebSocket
 * Sent when active users data is updated and broadcast to all clients
 */
interface ActiveUsersUpdatedMessage {
  type: 'active_users_updated';
  data: {
    [roomId: string]: {
      userCount: number;
      users: {
        [userId: string]: {
          username: string;
          joined_at: string;
        };
      };
    };
  };
  timestamp: string;
}

/**
 * Interface for changes reverted messages from WebSocket
 * Sent when a user cancels their changes and reverts them
 */
interface ChangesRevertedMessage {
  type: 'changes_reverted';
  data: {
    room: string;
    revertedBy: string;
    reverts: Record<string, any>;
  };
  timestamp: string;
}

/**
 * Interface for bulk jobs update messages from WebSocket
 * Used when multiple jobs are updated simultaneously (e.g., bulk upload)
 * Contains complete updated jobs data for immediate UI refresh
 */
interface BulkJobsUpdateMessage {
  type: 'bulk_jobs_updated';
  data: {
    jobs: JobData[];
    updatedBy: string;
    operation: 'bulk_upload';
    completeJobsData?: JobData[]; // Complete jobs data for immediate update
  };
}

/**
 * Interface for single job saved messages from WebSocket
 * Used when individual jobs are created or updated
 * Contains complete updated jobs data for immediate UI refresh
 */
interface SingleJobSavedMessage {
  type: 'single_job_saved';
  data: {
    job: JobData;
    updatedBy: string;
    operation: 'create' | 'update';
    completeJobsData?: JobData[]; // Complete jobs data for immediate update
  };
}

/**
 * Interface for jobs edited messages from WebSocket
 * Used when jobs within a specific room are edited
 * Contains complete updated jobs data for immediate UI refresh
 */
interface JobsEditedMessage {
  type: 'jobs_edited';
  data: {
    roomId: string;
    jobs: JobData[];
    updatedBy: string;
    operation: 'edit';
    completeJobsData?: JobData[]; // Complete jobs data for immediate update
  };
}

/**
 * Interface for job data structure
 * Represents the structure of job objects used throughout the application
 */
export interface JobData {
  id: string;
  jobId: string;
  priority: string;
  status: string;
  room: string;
  ndc: string;
  description: string;
  qtyOfContainers: number;
  qtyPerContainer: number;
  type: string;
  qtyUnitsToPackage: number;
  confirmedDate: Date;
  notes: any[];
  bpmProjected: number;
  bpmOverride: number | null;
  cleanProjected: number;
  cleanOverride: string | null;
  numberOfBottlesInAtShiftStart: number | null;
  jobStartTime: Date | null;
  [key: string]: any;
}

/**
 * Union type for all possible WebSocket message types
 * Used for type-safe message handling throughout the hook
 */
type WebSocketMessage =
  | JobUpdateMessage
  | JobCreatedMessage
  | JobDeletedMessage
  | RoomUpdateMessage
  | BulkJobsUpdateMessage
  | SingleJobSavedMessage
  | JobsEditedMessage
  | ConnectionStatusMessage
  | ErrorMessage
  | ConnectionEstablishedMessage
  | SubscriptionConfirmedMessage
  | HeartbeatResponseMessage
  | EditingStatusConfirmedMessage
  | UserIdentificationConfirmedMessage
  | ActiveUsersUpdatedMessage
  | ChangesRevertedMessage;

/**
 * Custom hook for managing WebSocket connections and job updates
 *
 * This hook provides a complete WebSocket integration for the Job Paste screen,
 * handling all real-time job updates, connection management, and user notifications.
 * It ensures that all connected users see job changes immediately without needing
 * to refresh the page or make additional API calls.
 *
 * Features:
 * - Automatic WebSocket connection management
 * - Real-time job updates from all users
 * - Smart notification filtering (no self-notifications)
 * - Editing conflict detection and prevention
 * - Complete jobs data updates for immediate UI refresh
 * - Error handling and connection status monitoring
 *
 * Returns:
 * - sendEditingStatus: Function to notify server of editing status
 * - isConnected: Boolean indicating WebSocket connection status
 *
 * Usage Example:
 * ```tsx
 * const { sendEditingStatus, isConnected } = useJobPasteWebSocket();
 *
 * // Notify server when user starts editing a job
 * sendEditingStatus('job_123', 'room_A', true);
 *
 * ```
 */
export const useJobPasteWebSocket = () => {
  const { JobPasteStore, UserStore } = useStore();
  const messageHandlerRef = useRef<((message: any) => void) | null>(null);

  // Track processed messages to prevent duplicate toasts
  const processedMessagesRef = useRef<Set<string>>(new Set());

  // Helper function to generate stable toast ID
  const generateToastId = (type: string, data: any): string => {
    switch (type) {
      case 'bulk_jobs_updated':
        return `bulk-jobs-updated-${data.updatedBy}-${data.jobs.length}-${data.jobs[0]?.jobId || 'unknown'}`;
      case 'single_job_saved':
        return `single-job-saved-${data.job.jobId}-${data.operation}-${data.updatedBy}`;
      case 'jobs_edited':
        return `jobs-edited-${data.roomId}-${data.jobs.length}-${data.updatedBy}`;
      case 'job_deleted':
        return `job-deleted-${data.jobId}-${data.updatedBy}`;
      default:
        return `${type}-${Date.now()}`;
    }
  };

  // Helper function to check if message was already processed
  const isMessageProcessed = (type: string, data: any): boolean => {
    const messageKey = generateToastId(type, data);
    if (processedMessagesRef.current.has(messageKey)) {
      return true;
    }
    // Add to processed set and clean up after 5 seconds
    processedMessagesRef.current.add(messageKey);
    setTimeout(() => {
      processedMessagesRef.current.delete(messageKey);
    }, 5000);
    return false;
  };

  /**
   * Handle individual job updates from WebSocket
   *
   * Processes job update messages and updates the UI accordingly.
   * Includes conflict detection for users currently editing jobs.
   *
   * @param data - Job update data containing job information and metadata
   */
  const handleJobUpdate = useCallback(
    (data: JobUpdateMessage['data']) => {
      // Don't update if user is currently editing this job
      if (JobPasteStore.isEditMode && JobPasteStore.currentRoomInEditMode === data.roomId) {
        const isEditingThisJob = JobPasteStore._cloned_jobs_for_edits?.some(
          room => room.id === data.roomId && room.jobs.some(job => job.jobId === data.jobId)
        );

        if (isEditingThisJob) {
          // Show notification about potential conflict
          toast.info(`Job ${data.jobId} has been updated by another user while you're editing.`, {
            toastId: `job-update-${data.jobId}`,
            autoClose: 5000
          });
          return;
        }
      }

      // Update the job in the store
      JobPasteStore.updateJobFromWebSocket(data);
    },
    [JobPasteStore]
  );

  /**
   * Handle job creation messages from WebSocket
   *
   * Processes new job creation and adds the job to the UI.
   * Shows a success notification to inform the user.
   *
   * @param data - Job creation data containing the new job information
   */
  const handleJobCreated = useCallback(
    (data: JobCreatedMessage['data']) => {
      JobPasteStore.addJobFromWebSocket(data);
      toast.success(`New job ${data.jobId} has been created.`, {
        toastId: `job-created-${data.jobId}`,
        autoClose: 3000
      });
    },
    [JobPasteStore]
  );

  /**
   * Handle job deletion messages from WebSocket
   *
   * Processes job deletion and updates the UI with complete jobs data.
   * Shows notification only for deletions from other users.
   *
   * @param data - Job deletion data containing job ID and complete updated jobs data
   */
  const handleJobDeleted = useCallback(
    (data: JobDeletedMessage['data']) => {
      // Use complete jobs data from WebSocket instead of calling API
      if (data.completeJobsData) {
        JobPasteStore.updateJobsFromWebSocket(data.completeJobsData);
      }

      // Only show toast for updates from other users
      const currentUser = UserStore.userMetadata?.userInfo?.name;
      if (data.updatedBy !== currentUser && !isMessageProcessed('job_deleted', data)) {
        toast.success(`Job ${data.jobId} has been deleted by ${data.updatedBy}.`, {
          toastId: generateToastId('job_deleted', data),
          autoClose: 5000
        });
      }
    },
    [JobPasteStore, UserStore]
  );

  /**
   * Handle room update messages from WebSocket
   *
   * Processes room-level updates and updates the UI accordingly.
   *
   * @param data - Room update data containing room information
   */
  const handleRoomUpdate = useCallback(
    (data: RoomUpdateMessage['data']) => {
      JobPasteStore.updateRoomFromWebSocket(data);
    },
    [JobPasteStore]
  );

  /**
   * Handle bulk jobs update messages from WebSocket
   *
   * Processes bulk job updates (e.g., bulk upload) and updates the UI with
   * complete jobs data. Shows notification only for updates from other users.
   *
   * @param data - Bulk jobs update data containing job array and complete jobs data
   */
  const handleBulkJobsUpdate = useCallback(
    (data: BulkJobsUpdateMessage['data']) => {
      // Use complete jobs data from WebSocket instead of calling API
      if (data.completeJobsData) {
        JobPasteStore.updateJobsFromWebSocket(data.completeJobsData);
      } else {
        // Fallback to old method if complete data is not available
        JobPasteStore.handleBulkJobsUpdate(data.jobs);
      }

      // Only show toast for updates from other users
      const currentUser = UserStore.userMetadata?.userInfo?.name;
      if (data.updatedBy !== currentUser && !isMessageProcessed('bulk_jobs_updated', data)) {
        toast.success(`Jobs have been bulk updated by ${data.updatedBy}.`, {
          toastId: generateToastId('bulk_jobs_updated', data),
          autoClose: 3000
        });
      }
    },
    [JobPasteStore, UserStore]
  );

  /**
   * Handle single job saved messages from WebSocket
   *
   * Processes individual job creation/update and updates the UI with
   * complete jobs data. Shows notification only for updates from other users.
   *
   * @param data - Single job saved data containing job info and complete jobs data
   */
  const handleSingleJobSaved = useCallback(
    (data: SingleJobSavedMessage['data']) => {
      // Use complete jobs data from WebSocket instead of calling API
      if (data.completeJobsData) {
        JobPasteStore.updateJobsFromWebSocket(data.completeJobsData);
      } else {
        // Fallback to old method if complete data is not available
        JobPasteStore.handleSingleJobSaved(data.job, data.operation);
      }

      // Only show toast for updates from other users
      const currentUser = UserStore.userMetadata?.userInfo?.name;
      if (data.updatedBy !== currentUser && !isMessageProcessed('single_job_saved', data)) {
        const operationText = data.operation === 'create' ? 'created' : 'updated';
        toast.success(`Job ${data.job.jobId} has been ${operationText} by ${data.updatedBy}.`, {
          toastId: generateToastId('single_job_saved', data),
          autoClose: 3000
        });
      }
    },
    [JobPasteStore, UserStore]
  );

  /**
   * Handle jobs edited messages from WebSocket
   *
   * Processes room-level job edits and updates the UI with complete jobs data.
   * Includes conflict detection for users currently editing the same room.
   * Shows notification only for updates from other users.
   *
   * @param data - Jobs edited data containing room ID, jobs array, and complete jobs data
   */
  const handleJobsEdited = useCallback(
    (data: JobsEditedMessage['data']) => {
      // Always call updateJobsFromWebSocket - it now handles edit mode logic internally
      if (data.completeJobsData) {
        JobPasteStore.updateJobsFromWebSocket(data.completeJobsData);
      } else {
        // Fallback to old method if complete data is not available
        JobPasteStore.handleJobsEdited(data.roomId, data.jobs);
      }

      // Show appropriate notifications based on edit mode and user
      const currentUser = UserStore.userMetadata?.userInfo?.name;
      const isCurrentUser = data.updatedBy === currentUser;
      const isEditingSameRoom =
        JobPasteStore.isEditMode && JobPasteStore.currentRoomInEditMode === data.roomId;

      if (!isCurrentUser && !isMessageProcessed('jobs_edited', data)) {
        if (isEditingSameRoom) {
          // Show conflict notification when editing the same room
          const room_name = JobPasteStore._cloned_jobs_for_edits?.find(
            room => room.id === data.roomId
          );
          toast.info(
            `Room ${room_name?.name} has been updated by ${data.updatedBy} while you're editing.`,
            {
              toastId: generateToastId('jobs_edited_conflict', data),
              autoClose: 8000
            }
          );
        } else {
          // Show normal update notification for other rooms
          toast.success(`Jobs have been updated by ${data.updatedBy}.`, {
            toastId: generateToastId('jobs_edited', data),
            autoClose: 3000
          });
        }
      }
    },
    [JobPasteStore, UserStore]
  );

  /**
   * Handle active users updated messages from WebSocket
   *
   * Processes active users updates and updates the UI with the new active users data.
   *
   * @param data - Active users update data containing room-specific user counts and lists
   */
  const handleActiveUsersUpdated = useCallback(
    (data: ActiveUsersUpdatedMessage['data']) => {
      JobPasteStore.updateActiveUsersFromWebSocket(data);
    },
    [JobPasteStore]
  );

  /**
   * Handle changes reverted messages from WebSocket
   *
   * Processes changes reverted messages and updates the UI when another user cancels their changes.
   * Shows a toast notification to inform the current user about the revert.
   *
   * @param data - Changes reverted data containing room, user, and reverted fields
   */
  const handleChangesReverted = useCallback(
    (data: ChangesRevertedMessage['data']) => {
      // Don't process our own reverts
      const currentUser = UserStore.userMetadata?.userInfo?.email;
      if (data.revertedBy === currentUser) {
        return;
      }

      // Apply the reverts to the store
      JobPasteStore.handleChangesReverted(data);
    },
    [JobPasteStore, UserStore]
  );

  /**
   * Handle active users messages from the active users WebSocket service
   *
   * Processes messages from the dedicated active users WebSocket service.
   *
   * @param message - Active users message from the service
   */
  const handleActiveUsersMessage = useCallback(
    (message: any) => {
      // Handle active users specific messages
      switch (message.type) {
        case 'active_users_updated':
          handleActiveUsersUpdated(message.data);
          break;
        case 'initial_active_users_state':
        case 'active_users_state':
          // Update the store with initial state
          if (message.data?.activeUsersByRoom) {
            JobPasteStore.updateActiveUsersFromWebSocket(message.data.activeUsersByRoom);
          }
          break;
        case 'connection_established':
          break;
        case 'subscription_confirmed':
          break;
        case 'error':
          toast.error(`Active Users WebSocket error: ${message.message}`, {
            toastId: `active-users-error-${Date.now()}`,
            autoClose: 5000
          });
          break;
        default:
          break;
      }
    },
    [handleActiveUsersUpdated, JobPasteStore]
  );

  /**
   * Main WebSocket message handler
   *
   * Routes incoming WebSocket messages to appropriate handlers based on message type.
   * Provides centralized error handling and logging for all WebSocket communication.
   *
   * @param message - WebSocket message object with type and data
   */
  const handleWebSocketMessage = useCallback(
    (message: WebSocketMessage) => {
      switch (message.type) {
        case 'job_updated':
          handleJobUpdate(message.data);
          break;

        case 'job_created':
          handleJobCreated(message.data);
          break;

        case 'job_deleted':
          handleJobDeleted(message.data);
          break;

        case 'room_updated':
          handleRoomUpdate(message.data);
          break;

        case 'bulk_jobs_updated':
          handleBulkJobsUpdate(message.data);
          break;

        case 'single_job_saved':
          handleSingleJobSaved(message.data);
          break;

        case 'jobs_edited':
          handleJobsEdited(message.data);
          break;

        case 'active_users_updated':
          handleActiveUsersUpdated(message.data);
          break;

        case 'changes_reverted':
          handleChangesReverted(message.data);
          break;

        case 'connection_status':
          break;

        case 'error':
          // Handle error messages from the backend
          // Optionally show a toast notification for user-facing errors
          if (message.message && !message.message.includes('Unknown message type')) {
            toast.error(`WebSocket error: ${message.message}`, {
              toastId: `websocket-error-${Date.now()}`,
              autoClose: 5000
            });
          }
          break;

        case 'connection_established':
          break;

        case 'subscription_confirmed':
          break;

        case 'heartbeat_response':
          // Handle heartbeat responses silently
          break;

        case 'editing_status_confirmed':
          break;

        case 'user_identification_confirmed':
          break;
      }
    },
    [
      handleJobUpdate,
      handleJobCreated,
      handleJobDeleted,
      handleRoomUpdate,
      handleBulkJobsUpdate,
      handleSingleJobSaved,
      handleJobsEdited,
      handleActiveUsersUpdated,
      handleChangesReverted
    ]
  );

  /**
   * Send editing status to WebSocket server
   *
   * Notifies the server when a user starts or stops editing a job.
   * This enables collaborative editing features and conflict detection.
   *
   * @param jobId - ID of the job being edited
   * @param roomId - ID of the room containing the job
   * @param isEditing - Whether the user is starting (true) or stopping (false) editing
   */
  const sendEditingStatus = useCallback(
    (jobId: string, roomId: string, isEditing: boolean) => {
      globalWebSocketService.send({
        type: isEditing ? 'start_editing' : 'stop_editing',
        jobId,
        roomId,
        userId: UserStore.userMetadata?.userInfo?.name
      });
    },
    [UserStore.userMetadata?.userInfo?.name]
  );

  /**
   * Send active user update to WebSocket server
   *
   * Notifies the server when a user starts or stops editing a room.
   * This enables real-time active user count display and collaborative awareness.
   *
   * @param roomId - ID of the room being edited
   * @param action - "add" when starting to edit, "remove" when stopping
   */
  const sendActiveUserUpdate = useCallback(
    async (roomId: string, action: 'add' | 'remove') => {
      const messageData = {
        type: 'get_active_users',
        action,
        roomId,
        userId: UserStore.userMetadata?.userInfo?.email || UserStore.userMetadata?.userInfo?.name,
        username: UserStore.userMetadata?.userInfo?.name
      };

      // Use retry logic for better reliability
      const success = await activeUsersWebSocketService.sendActiveUserUpdate(messageData);

      if (!success) {
        // Optionally show a toast notification to the user
        toast.warning(
          `Failed to update active users status. Please refresh if you don't see other users.`,
          {
            toastId: `active-users-update-failed-${roomId}-${action}`,
            autoClose: 5000
          }
        );
      }
    },
    [UserStore.userMetadata?.userInfo?.email, UserStore.userMetadata?.userInfo?.name]
  );

  /**
   * Initialize WebSocket message handlers for JobPaste screen
   *
   * Sets up message handlers for the JobPaste screen when the component mounts.
   * The job-updates WebSocket connection is handled by the JobPaste component,
   * while the active-users WebSocket is handled globally.
   */
  useEffect(() => {
    // Create message handler for this hook instance
    messageHandlerRef.current = handleWebSocketMessage;

    // Register message handlers with both services
    // Note: active-users WebSocket is already connected globally
    globalWebSocketService.addMessageHandler(messageHandlerRef.current);
    activeUsersWebSocketService.addMessageHandler(handleActiveUsersMessage);

    // Cleanup on unmount
    return () => {
      if (messageHandlerRef.current) {
        globalWebSocketService.removeMessageHandler(messageHandlerRef.current);
        activeUsersWebSocketService.removeMessageHandler(handleActiveUsersMessage);
        messageHandlerRef.current = null;
      }
    };
  }, [handleWebSocketMessage, handleActiveUsersMessage]);

  return {
    sendEditingStatus,
    sendActiveUserUpdate,
    isConnected: globalWebSocketService.isConnected && activeUsersWebSocketService.isConnected,
    isJobUpdatesConnected: globalWebSocketService.isConnected,
    isActiveUsersConnected: activeUsersWebSocketService.isConnected
  };
};
